import Resources from "../../../../data/services/resources";
import {DEFAULT_METADATA_SELECT_SEARCH_QUERY, READ_PERM} from "../../../util/util-consts";
import {Field, FieldsManager} from "../../../../data/services/fields";
import moment from "moment/moment";
import ModalDefault from "../modal-default";
import InvoiceExpenseDetailsDialog from "../invoice-expense-details-dialog";
import {getThirdResource} from "../../../../data/actions/thirdResource";
import LocalStorage from "../../../../util/localStorage";
import ResourceTableDialog from "../resource-table-dialog";
import React, {useEffect, useState} from "react";
import {checkPerm, classNames, getLookup, getProp} from "../../../util/util-helpers";
import CopyToClipboardButton from "../../buttons/copy-to-clipboard-button";
import {fieldsToHtml, fillFieldsFromData, includeFields} from "../../../util/util-fields";
import {Loader} from "../../loader";
import {ChevronRightIcon} from "@heroicons/react/20/solid";
import {InformationCircleIcon} from "@heroicons/react/24/outline";
import Tippy from "@tippyjs/react";
import {formatMoney} from "../../../util/util-formaters";

/**
 * One figure in the summary block, with its explanation on hover.
 *
 * Every figure here is lifetime-to-date: inventoryReport builds them from
 * getStockForIDs(), which scopes on company and product only. The date and customer
 * filters below the block apply to the movement list, nothing else. That is the single
 * thing users misread, so each tooltip says it in its own words.
 */
function SummaryFigure({label, value, tip, className}) {
    return (
        <div className={className}>
            <Tippy content={<div className="text-xs p-1 max-w-xs space-y-1">{tip}</div>} maxWidth={360}>
                <span className="inline-flex items-center gap-1.5 cursor-help">
                    <span>{label}:</span>
                    <span className="font-semibold">{value}</span>
                    <InformationCircleIcon className="w-4 h-4 text-tm-gray-400"/>
                </span>
            </Tippy>
        </div>
    )
}

export default function InventoryReportDialog({
                                                  isReportDialogOpen,
                                                  dialogResource,
                                                  resource,
                                                  dispatch,
                                                  thirdResource,
                                                  translate,
                                                  setIsReportDialogOpen,
                                                  selectedReportItem,
                                                  startDate,
                                                  endDate
                                              }) {

    const [isExpanded, setIsExpanded] = useState(true);
    const data = getProp(dialogResource, "data.ProductService", []);

    // The movement ledger below defaults to the current calendar year. A caller whose own
    // report looks at a different span (a rolling window, a custom date range) passes its
    // span in, otherwise the ledger can open empty while the totals above it look right.
    const toFilterDate = (value, fallback, edge) => {
        const parsed = value ? moment(value) : null;

        if (!parsed || !parsed.isValid()) {
            return fallback.format('YYYY-MM-DD HH:mm:ss');
        }

        // A bare YYYY-MM-DD parses to midnight, which would drop everything recorded later
        // that day, so an end bound is pushed out to the end of its day.
        return (edge === 'end' ? parsed.endOf('day') : parsed.startOf('day')).format('YYYY-MM-DD HH:mm:ss');
    };

    const filterStartDate = toFilterDate(startDate, moment().startOf('year'), 'start');
    const filterEndDate = toFilterDate(endDate, moment().endOf('year'), 'end');

    useEffect(() => {
        !dialogResource.isLoading && setFields(getFields(data))
    }, [dialogResource.isLoading])

    const getFields = (item) => {
        let fieldTemplates = {
            ProductService: new Field('ProductService', "", ['empty'], true, 'text', {
                addContainerClass: "col-span-6",
                label: "name"
            }),
            SKU: new Field('SKU', '', [''], true, 'text', {addContainerClass: "col-span-6", label: 'sku'}),
            ProductServiceCategoryID: new Field('ProductServiceCategoryID', '', [''], true, 'select-search', {
                addContainerClass: "col-span-4",
                // htmlAfterField: (it) => {
                //     return <Button
                //         disabled={it.disabled}
                //         onClick={() => this.setState({
                //             isCategoryListModalOpen: true
                //         })}
                //         className="btn btn-input"
                //     >
                //         {it?.value?.value ? <ArrowPathIcon/> : <PlusIcon/>}
                //     </Button>
                // }
            }, {placeholder: ' '}),
            ProductServicePartLocationID: new Field('ProductServicePartLocationID', '', [''], true, 'select-search', {
                addContainerClass: "col-span-4",
                // htmlAfterField: (it) => {
                //     return <Button
                //         disabled={it.disabled}
                //         onClick={() => this.setState({
                //             isLocationsListModalOpen: true
                //         })}
                //         className="btn btn-input"
                //     >
                //         {it?.value?.value ? <ArrowPathIcon/> : <PlusIcon/>}
                //     </Button>
                // }
            }, {placeholder: ' '}),
            UomID: new Field('UomID', '', [''], true, 'select', {addContainerClass: "col-span-4"}, {
                placeholder: ' ',
                values: getLookup('Uom')
            }),
            ManufacturerID: new Field('ManufacturerID', '', [], true, 'select-search',
                {
                    addContainerClass: 'col-span-6',
                    // htmlAfterField: (it) => {
                    //     return <Button
                    //         disabled={it.disabled}
                    //         onClick={() => this.setState({
                    //             isManufacturerListModalOpen: true
                    //         })}
                    //         className="btn btn-input"
                    //     >
                    //         {it?.value?.value ? <ArrowPathIcon/> : <PlusIcon/>}
                    //     </Button>
                    // }
                }, {placeholder: ' '}),
            QRCode: new Field('QRCode', '', [''], true, 'text', {
                addContainerClass: 'col-span-6',
            }),
            OEMNumber: new Field('OEMNumber', '', [''], true, 'text', {
                addContainerClass: 'col-span-6',
            }),
            CrossRefNumber: new Field('CrossRefNumber', '', [''], true, 'creatable-csv', {
                fieldOptions: (it) => {
                    return (
                        it.value
                            ? <div className="gap-x-3 h-5 relative bottom-0.5 flex">
                                <CopyToClipboardButton
                                    clipboardText={(it.value ? it.value : []).reduce((memo, it) => {
                                        memo.push(it.value);
                                        return memo;
                                    }, []).join(", ")}
                                    addClass="relative -top-2"
                                    translate={translate}
                                />
                            </div>
                            : null
                    )
                },
                addContainerClass: 'col-span-6',
                onEnterDown: true
            }, {placeholder: ' ', isDisabled: true}),
            IncomePrice: new Field('IncomePrice', '', [''], true, 'money', {addContainerClass: 'col-span-3'}),
            ExpensePrice: new Field('ExpensePrice', '', [''], true, 'money', {addContainerClass: 'col-span-3'}),
            ReorderPoint: new Field('ReorderPoint', '', [''], true, 'integer', {
                addContainerClass: "col-span-3",
                placeholder: "0",
                label: translate("MinRecommendedStock")
            }),
            MaxRecommendedStock: new Field('MaxRecommendedStock', '', [''], true, 'integer', {
                addContainerClass: "col-span-3",
                placeholder: "0"
            }),
        }

        if (item.ProductServiceTypeID !== 1) {
            fieldTemplates = includeFields(fieldTemplates, ['ProductService', 'SKU'])
        }

        return fillFieldsFromData(fieldTemplates, item);
    }

    const [fields, setFields] = useState({});
    const [isInvoiceExpenseDialogOpen, setIsInvoiceExpenseDialogOpen] = useState(false);
    const [selectedInvoiceExpenseItem, setSelectedInvoiceExpenseItem] = useState({});

    const handleInvoiceExpenseClick = (item = null) => {
        setSelectedInvoiceExpenseItem(item);
        setIsInvoiceExpenseDialogOpen(true);
    }

    const handleInputChange = (name, value) => {
        setFields(FieldsManager.updateField(fields, name, value))
    };

    const fieldsHTML = fieldsToHtml(Object.values(Object.assign({}, fields)), translate, handleInputChange, {})

    return (
        <ResourceTableDialog
            // ResourceTableDialog copies fieldsFilter into state in its constructor and never
            // reads the prop again, so a caller whose date range changes needs a remount to
            // pick it up. Callers that leave the dates alone keep a constant key and never
            // remount.
            key={filterStartDate + '|' + filterEndDate}
            widthClass={'max-w-6xl'}
            show={isReportDialogOpen}
            dialogResource={dialogResource}
            resource={resource}
            title={'Inventory report'}
            dispatch={dispatch}
            isFooterVisible={true}
            onView={(item) => {
                if (item.Type === 1 && !checkPerm(Resources.ExpenseExpense, READ_PERM)) {
                    return false;
                }
                if (item.Type === 2 && !checkPerm(Resources.InvoicesInvoice, READ_PERM)) {
                    return false;
                }
                if (item.Type === 3 || item.Type === 4 || item.Type === 5) {
                    return false;
                }
                handleInvoiceExpenseClick(item)
            }}
            hasViewPerm={checkPerm(Resources.ExpenseExpense, READ_PERM) && checkPerm(Resources.InvoicesInvoice, READ_PERM)}
            defaultAction={(item) => {
                if (item.Type === 1 && !checkPerm(Resources.ExpenseExpense, READ_PERM)) {
                    return false;
                }
                if (item.Type === 2 && !checkPerm(Resources.InvoicesInvoice, READ_PERM)) {
                    return false;
                }
                if (item.Type === 3 || item.Type === 4 || item.Type === 5) {
                    return false;
                }

                handleInvoiceExpenseClick(item)
            }}
            limit={50}
            fields={{
                Date: new Field('Date', '', [], false, 'date', {
                    omitSort: true
                }),
                Description: new Field('Description', '', [''], false, 'textarea', {omitSort: true}),
                Type: new Field('Type', '', [''], false, 'custom', {
                    render: (it) => {
                        switch (it.Type) {
                            case 1:
                                return 'Bought';
                            case 2:
                                return 'Sold';
                            case 3:
                                return 'Vendor credit';
                            case 4:
                                return 'Credit memo';
                            case 5:
                                return 'Adjustment';
                        }
                    },
                    omitSort: true
                }),
                FromTo: new Field('FromTo', '', [''], false, 'custom', {
                    render: (it) => it.FromTo,
                    omitSort: true
                }),
                Amount: new Field('Amount', '', [''], false, 'money', {omitSort: true}),
                Qty: new Field('Qty', '', [''], false, 'custom', {
                    render: (it) => {
                        const rawQty = Number(it.Qty) || 0;
                        // Bought and Credit memo bring stock in, Sold and Vendor credit take it out.
                        // Adjustments keep whatever sign the server sends.
                        const signedQty = [2, 3].includes(it.Type)
                            ? -Math.abs(rawQty)
                            : [1, 4].includes(it.Type)
                                ? Math.abs(rawQty)
                                : rawQty;

                        if (!signedQty) {
                            return <span className="text-tm-gray-700">{rawQty}</span>
                        }

                        return (
                            <span
                                className={classNames("font-semibold", signedQty < 0 ? "text-red-600" : "text-green-600")}
                            >
                                {signedQty > 0 ? "+" : "-"}{Math.abs(signedQty)}
                            </span>
                        )
                    },
                    omitSort: true
                }),
                TaxID: new Field('TaxID', '', [''], false, 'select-search', {omitSort: true}),
                TotalAmount: new Field('TotalAmount', '', [''], false, 'money', {omitSort: true})
            }}

            fieldsFilter={{
                StartDate: new Field('StartDate', filterStartDate, [''], false, 'date', {addContainerClass: 'col-span-4'}, {}),
                EndDate: new Field('EndDate', filterEndDate, [''], false, 'date', {addContainerClass: 'col-span-4'}, {}),
                OrganizationID: new Field('OrganizationID', '', [''], false, 'select-search', {addContainerClass: 'col-span-4'}, {
                    isClearable: true
                }),
            }}

            htmlBefore={(
                <div className={"p-2 text-lg"}>
                    <div className="p-5">

                        <button onClick={() => setIsExpanded(!isExpanded)}
                                className={"border rounded-sm border-primary"}
                        >
                            <ChevronRightIcon
                                className={
                                    classNames(
                                        "w-5 h-5",
                                        isExpanded ? "rotate-90" : undefined
                                    )
                                }
                            />
                        </button>

                        {!dialogResource.isLoading && !!isExpanded && (
                            <div className="grid grid-cols-12 gap-4">
                                {fieldsHTML}
                            </div>
                        )}

                        {dialogResource.isLoading && (
                            <div className={"flex justify-center"}>
                                <Loader disableContainer={true}/>
                            </div>
                        )}
                    </div>
                    <div className={"px-4 grid grid-cols-6 gap-2"}>
                        {/* The opening balances are seed figures typed once when the part
                            was created. They are not something anyone acts on, so they
                            live inside the two tooltips that actually build on them
                            rather than taking a line of their own. */}
                        <SummaryFigure
                            className={'col-span-3'}
                            label="Current stock"
                            value={dialogResource?.data?.CurrentStock ?? "/"}
                            tip={(
                                <>
                                    <p>Everything on the shelf today.</p>
                                    <p>It starts from the opening count
                                        of <span className="font-semibold">{dialogResource?.data?.InventoryCount ?? 0}</span> entered
                                        when the part was created, then adds every purchase, sale, vendor credit,
                                        credit memo and adjustment since.</p>
                                </>
                            )}
                        />

                        <SummaryFigure
                            className={'col-span-3'}
                            label="Current inventory value"
                            value={formatMoney(dialogResource?.data?.CurrentInventoryValue ?? 0)}
                            tip={(
                                <>
                                    <p>What the books carry this stock at.</p>
                                    <p>It starts from the opening value
                                        of <span className="font-semibold">{formatMoney(dialogResource?.data?.InventoryValue ?? 0)}</span> entered
                                        when the part was created, then adds the journal balance on the inventory
                                        asset account.</p>
                                    <p>This is the accounting figure, not a fresh calculation from the documents
                                        below, so the two can drift apart if a journal entry was ever wrong.</p>
                                </>
                            )}
                        />

                        <SummaryFigure
                            className={'col-span-6'}
                            label="Per item value"
                            value={formatMoney(dialogResource?.data?.ItemValue ?? 0)}
                            tip={(
                                <>
                                    <p>What one unit on the shelf is carried at: inventory value divided by stock
                                        on hand.</p>
                                    <p>Compare it against Avg buy price. A gap means either that older stock was
                                        bought at a different price, or that a journal entry for this part is off.</p>
                                </>
                            )}
                        />

                        <SummaryFigure
                            className={'col-span-3'}
                            label="Avg buy price"
                            value={formatMoney(dialogResource?.data?.AverageBuyPrice ?? 0)}
                            tip={(
                                <>
                                    <p>What you have paid per unit, across every purchase ever made for this
                                        part.</p>
                                    <p>Total spent divided by total units bought. The dates below do not change
                                        it.</p>
                                </>
                            )}
                        />

                        <SummaryFigure
                            className={'col-span-3'}
                            label="Avg sell price"
                            value={formatMoney(dialogResource?.data?.AverageSellPrice ?? 0)}
                            tip={(
                                <>
                                    <p>What you have charged per unit, across every sale ever made for this
                                        part.</p>
                                    <p>Total charged divided by total units sold. The gap against Avg buy price is
                                        your margin on this part.</p>
                                </>
                            )}
                        />

                        <div className={'col-span-6 text-sm text-tm-gray-500'}>
                            These figures cover the part's whole history. The dates and customer below filter the
                            movement list only.
                        </div>

                        <div className={'col-span-6 text-sm text-tm-gray-500'}>
                            Quantity: Bought and Credit memo are shown as <span
                            className="text-green-600 font-semibold">plus</span>, Sold and Vendor credit as <span
                            className="text-red-600 font-semibold">minus</span>.
                        </div>
                    </div>
                </div>
            )}

            tableKey="Name"
            defaultQuery={{id: selectedReportItem?.ProductServiceID ?? null}}
            onClose={() => {
                setIsReportDialogOpen(false);
                setIsExpanded(true);
            }}
            translate={translate}
            selects={{
                OrganizationID: {
                    api: 'api/' + Resources.OrganizationsQuick,
                    query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                    searchMap: (item) => ({
                        value: item.OrganizationID,
                        label: item.LegalName,
                        Organization: item
                    })
                }
            }}
            htmlAfter={() => {
                return <ModalDefault
                    show={isInvoiceExpenseDialogOpen}
                    widthClass={'max-w-5xl w-screen'}
                    title={"Details"}

                    closeButtonLabel={translate('btn.Close')}
                    onClose={() => setIsInvoiceExpenseDialogOpen(false)}
                >
                    <InvoiceExpenseDetailsDialog
                        resource={thirdResource}
                        isLoading={thirdResource?.isLoading}
                        translate={translate}
                        dispatch={dispatch}
                        showLoadReferenceLinks={true}

                        disableComments={true}
                        canDeletePayment={false}
                        disableAddReference={true}
                        disableTransactionImport={true}

                        // Data events
                        onFetchData={() => {
                            if (selectedInvoiceExpenseItem?.ID) {
                                dispatch(getThirdResource({
                                    user: LocalStorage.get('user'),
                                    query: {
                                        [selectedInvoiceExpenseItem.Type === 1 ? "ExpenseID" : "InvoiceID"]: selectedInvoiceExpenseItem.ID
                                    },
                                    resource: selectedInvoiceExpenseItem.Type === 1 ? Resources.ExpenseExpense : Resources.InvoicesInvoice
                                }))
                            }
                        }}
                    />
                </ModalDefault>
            }}
        />
    )
}